import { faArrowRightLong } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography } from "@tosize/ui";
import { Link, type LinkProps } from "~/components/Link";
import Image from "~/components/utilities/Image";

interface Props {
	title: string;
	ctaTitle: string;
	image: string;
	href: LinkProps["href"];
}

export function MainCategoryCtaItem({ title, ctaTitle, image, href }: Props) {
	return (
		<Link
			className="flex aspect-video xs:aspect-square flex-col overflow-hidden rounded-xl border border-shades hover:shadow focus:outline-none focus-visible:shadow motion-safe:transition-shadow sm:aspect-video md:aspect-auto"
			href={href}
		>
			<div className="relative grow">
				<Image src={image} alt={title} fill priority className="object-cover" />
			</div>

			<div className="flex flex-col gap-1 p-4">
				<Typography variant="paragraph-bold" color="text-primary-medium" className="leading-tight">
					{title}
				</Typography>

				<Typography className="line-clamp-2 leading-tight">{ctaTitle}</Typography>
			</div>
		</Link>
	);
}

interface MainCategoryCtaBigItemProps extends Props {
	label: string;
	subTitle?: string;
}

export function MainCategoryCtaBigItem(props: MainCategoryCtaBigItemProps) {
	const { title, subTitle, ctaTitle, image, href, label } = props;

	return (
		<Link
			href={href}
			title={title}
			className="group relative xs:col-span-2 overflow-hidden rounded-xl hover:shadow focus:outline-none focus-visible:shadow motion-safe:transition-shadow"
		>
			<Image src={image} alt={title} fill priority className="object-cover" />

			<div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />

			<div className="relative flex h-full flex-col items-start px-8 py-6">
				<div className="mb-2 flex items-center justify-center rounded-full bg-white px-6 py-0.5 font-bold text-primary-medium lg:mb-4 lg:px-8 lg:py-1 lg:text-lg">
					{label}
				</div>

				<Typography
					variant="paragraph-bold"
					component="span"
					className="mb-2 inline-block text-2xl text-white leading-[1.1] sm:max-w-[70%] lg:text-3xl 2xl:max-w-[65%]"
				>
					{title}
				</Typography>

				{!!subTitle && (
					<Typography
						variant="paragraph-bold"
						component="span"
						className="mb-2 inline-block text-white text-xl leading-[1.1] sm:max-w-[60%] lg:mb-4 lg:text-2xl 2xl:mb-6 2xl:max-w-[55%]"
					>
						{subTitle}
					</Typography>
				)}

				<Typography className="mt-auto font-bold text-primary group-hover:underline group-focus-visible:underline lg:text-lg">
					<span className="mr-2">{ctaTitle}</span>
					<span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-shades-medium-light text-dark text-sm">
						<FontAwesomeIcon icon={faArrowRightLong} fixedWidth />
					</span>
				</Typography>
			</div>
		</Link>
	);
}

export interface CategoryCtaButtonProps extends Omit<Props, "href"> {
	onClick?: () => void;
}

export function CategoryCtaButton({ title, ctaTitle, image, onClick }: CategoryCtaButtonProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="flex aspect-video xs:aspect-square flex-col overflow-hidden rounded-xl border border-shades text-left hover:shadow focus:outline-none focus-visible:shadow motion-safe:transition-shadow sm:aspect-video md:aspect-auto"
		>
			<div className="relative grow">
				<Image src={image} alt={title} fill className="object-cover" />
			</div>

			<div className="flex flex-col gap-1 p-4">
				<Typography variant="paragraph-bold" color="text-primary-medium" className="leading-tight">
					{title}
				</Typography>
				<Typography className="line-clamp-2 leading-tight">{ctaTitle}</Typography>
			</div>
		</button>
	);
}
